import { useState } from "react";
import { ChevronDown } from "lucide-react";

import { Sparkline } from "./Sparkline.jsx";

/**
 * What the box is doing right now, and what it has been doing for the last
 * quarter of an hour.
 *
 * Three resources, because those are the three that make a box unhappy: CPU,
 * memory and disk. Disk gets a bar rather than a chart — it moves over days, and
 * a flat line for fifteen minutes would be a chart that says nothing.
 *
 * On a phone this collapses to a single line of numbers. Three charts stacked is
 * most of a screen before you have seen a single process, and the numbers are
 * what you check first anyway; the shapes are one tap away.
 */

function gb(bytes) {
    if (bytes === null || bytes === undefined) return "—";
    const value = bytes / 1e9;
    return value >= 10 ? value.toFixed(0) : value.toFixed(1);
}

/** Quiet until it matters, then it says so on its own. */
function toneFor(ratio) {
    if (ratio === null) return { text: "", chart: "text-gray-400 dark:text-gray-500", bar: "bg-gray-400 dark:bg-gray-500" };
    if (ratio > 0.9) return { text: "text-red-600 dark:text-red-400", chart: "text-red-500", bar: "bg-red-500" };
    if (ratio > 0.75) return { text: "text-amber-600 dark:text-amber-400", chart: "text-amber-500", bar: "bg-amber-500" };
    return { text: "", chart: "text-blue-500", bar: "bg-gray-400 dark:bg-gray-500" };
}

export function ResourceBar({ system, history }) {
    const [open, setOpen] = useState(false);

    const samples = history?.samples ?? [];
    const latest = samples.at(-1);

    const cpu = latest?.cpu ?? null;
    const cpuTone = toneFor(cpu === null ? null : cpu / 100);

    const memUsed = latest?.memUsed ?? system?.memory?.usedBytes ?? null;
    const memTotal = latest?.memTotal ?? system?.memory?.totalBytes ?? null;
    const memRatio = memUsed && memTotal ? memUsed / memTotal : null;
    const memTone = toneFor(memRatio);

    const diskUsed = system?.disk?.usedBytes ?? latest?.diskUsed ?? null;
    const diskTotal = system?.disk?.totalBytes ?? latest?.diskTotal ?? null;
    const diskRatio = diskUsed && diskTotal ? diskUsed / diskTotal : null;
    const diskTone = toneFor(diskRatio);

    const swapUsed = system?.memory?.swapUsedBytes ?? 0;

    if (!system) return <div className="h-16 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-900" />;

    return (
        <section className="card overflow-hidden">
            {/* One line on a phone; the same line is the header on a desktop. */}
            <button
                onClick={() => setOpen((value) => !value)}
                aria-expanded={open}
                className="flex w-full items-center gap-4 px-4 py-2.5 text-left md:cursor-default md:pb-0"
            >
                <Reading label="CPU" value={cpu === null ? "—" : `${Math.round(cpu)}%`} tone={cpuTone.text} />
                <Reading label="Memory" value={`${gb(memUsed)}/${gb(memTotal)} GB`} tone={memTone.text} />
                <Reading label="Disk" value={`${gb(diskUsed)}/${gb(diskTotal)} GB`} tone={diskTone.text} />
                {swapUsed > 100e6 ? <Reading label="Swap" value={`${gb(swapUsed)} GB`} tone="text-amber-600 dark:text-amber-400" /> : null}

                <ChevronDown
                    size={16}
                    className={`muted ml-auto shrink-0 transition-transform md:hidden ${open ? "rotate-180" : ""}`}
                    aria-hidden
                />
            </button>

            <div className={`${open ? "block" : "hidden"} md:block`}>
                <div className="grid gap-x-8 gap-y-4 border-t border-gray-100 px-4 py-3 sm:grid-cols-2 lg:grid-cols-3 md:border-t-0 dark:border-gray-800">
                    <Panel
                        label="CPU"
                        detail={`${history?.cores ?? "?"} cores · load ${latest?.load?.toFixed(2) ?? "—"}`}
                        chart={<Sparkline values={samples.map((s) => s.cpu)} max={100} tone={cpuTone.chart} />}
                    />
                    <Panel
                        label="Memory"
                        detail={`${gb(memTotal - memUsed)} GB available${swapUsed > 1e6 ? ` · ${gb(swapUsed)} GB swap` : ""}`}
                        chart={<Sparkline values={samples.map((s) => s.memUsed)} max={memTotal} tone={memTone.chart} />}
                    />
                    <Panel
                        label="Disk"
                        detail={`${gb(diskTotal - diskUsed)} GB free`}
                        chart={
                            // A bar, not a chart: disk moves over days, so fifteen
                            // minutes of it would be a flat line pretending to inform.
                            <div className="flex h-7 items-center">
                                <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                                    <div className={`h-full ${diskTone.bar}`} style={{ width: `${Math.min((diskRatio ?? 0) * 100, 100)}%` }} />
                                </div>
                            </div>
                        }
                    />
                </div>

                {samples.length > 1 ? (
                    <p className="muted px-4 pb-3 text-xs">
                        Last {Math.round(((samples.at(-1).t - samples[0].t) / 60000) || 0)} minutes, sampled every{" "}
                        {Math.round((history?.intervalMs ?? 5000) / 1000)}s.
                    </p>
                ) : null}
            </div>
        </section>
    );
}

function Reading({ label, value, tone }) {
    return (
        <span className="flex min-w-0 items-baseline gap-1.5">
            <span className="muted text-xs">{label}</span>
            <span className={`text-sm font-medium tabular-nums ${tone}`}>{value}</span>
        </span>
    );
}

function Panel({ label, detail, chart }) {
    return (
        <div className="min-w-0">
            <div className="mb-1 flex items-baseline justify-between gap-2">
                <span className="text-xs font-medium">{label}</span>
                <span className="muted truncate text-xs tabular-nums">{detail}</span>
            </div>
            {chart}
        </div>
    );
}
